/*
 * Billing hooks (Area B): summary, payment methods (read only), billing details, invoices, payments.
 * Paying and managing payment methods happen at Dodo (src/api/hooks/checkout.ts). When the browser comes
 * back from Dodo, everything a payment can touch is refreshed (`refreshBilling`): billing, subscriptions,
 * servers, AI credits, the bell and the session (its organisation summary drives the dunning and trial
 * banners).
 */
import {
  keepPreviousData,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query'
import { api } from '@/api/client'
import type {
  BillingDetails,
  BillingDetailsInput,
  BillingSummary,
  Invoice,
  InvoiceListQuery,
  Page,
  Payment,
  PaymentListQuery,
  PaymentMethod,
} from '@/api/types'
import { i18n } from '@/i18n'
import { authKeys } from './auth'
import { notificationKeys } from './notifications'
import { serverKeys } from './servers'
import { subscriptionKeys } from './subscriptions'

export const billingKeys = {
  all: ['billing'] as const,
  summary: () => [...billingKeys.all, 'summary'] as const,
  methods: () => [...billingKeys.all, 'payment-methods'] as const,
  details: () => [...billingKeys.all, 'details'] as const,
  invoices: (q: InvoiceListQuery = {}) => [...billingKeys.all, 'invoices', q] as const,
  invoice: (id: string) => [...billingKeys.all, 'invoice', id] as const,
  payments: (q: PaymentListQuery = {}) => [...billingKeys.all, 'payments', q] as const,
  payment: (id: string) => [...billingKeys.all, 'payment', id] as const,
}

/** Everything a payment or a plan change can change. */
export async function refreshBilling(qc: QueryClient) {
  await Promise.all([
    qc.invalidateQueries({ queryKey: billingKeys.all }),
    qc.invalidateQueries({ queryKey: subscriptionKeys.all }),
    qc.invalidateQueries({ queryKey: serverKeys.all }),
    qc.invalidateQueries({ queryKey: ['ai-credits'] }),
    qc.invalidateQueries({ queryKey: notificationKeys.all }),
    qc.invalidateQueries({ queryKey: authKeys.session }),
  ])
}

export const billingSummaryQuery = () =>
  queryOptions({
    queryKey: billingKeys.summary(),
    queryFn: ({ signal }) => api.get<BillingSummary>('/billing/summary', { signal }),
  })

/** The billing overview: next charge, amount due, default method, alerts. */
export function useBillingSummary() {
  return useQuery(billingSummaryQuery())
}

// ---------------------------------------------------------------------------------------------------------
// Payment methods

export const paymentMethodsQuery = () =>
  queryOptions({
    queryKey: billingKeys.methods(),
    queryFn: ({ signal }) => api.get<PaymentMethod[]>('/billing/payment-methods', { signal }),
  })

export function usePaymentMethods(opts: { enabled?: boolean } = {}) {
  return useQuery({ ...paymentMethodsQuery(), enabled: opts.enabled ?? true })
}

// ---------------------------------------------------------------------------------------------------------
// Billing details

export const billingDetailsQuery = () =>
  queryOptions({
    queryKey: billingKeys.details(),
    queryFn: ({ signal }) => api.get<BillingDetails>('/billing/details', { signal }),
  })

export function useBillingDetails() {
  return useQuery(billingDetailsQuery())
}

export function useUpdateBillingDetails() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: BillingDetailsInput) => api.put<BillingDetails>('/billing/details', body),
    onSuccess: (d) => {
      qc.setQueryData(billingKeys.details(), d)
      return Promise.all([
        qc.invalidateQueries({ queryKey: billingKeys.summary() }),
        qc.invalidateQueries({ queryKey: ['organization'] }),
      ])
    },
    meta: { silent: true, success: i18n.t('billing:details.saved') },
  })
}

// ---------------------------------------------------------------------------------------------------------
// Invoices and payments

export const invoicesQuery = (q: InvoiceListQuery = {}) =>
  queryOptions({
    queryKey: billingKeys.invoices(q),
    queryFn: ({ signal }) => api.get<Page<Invoice>>('/billing/invoices', { query: q, signal }),
    placeholderData: keepPreviousData,
  })

export function useInvoices(q: InvoiceListQuery = {}) {
  return useQuery(invoicesQuery(q))
}

export const invoiceQuery = (id: string) =>
  queryOptions({
    queryKey: billingKeys.invoice(id),
    queryFn: ({ signal }) => api.get<Invoice>(`/billing/invoices/${id}`, { signal }),
  })

export function useInvoice(id: string | null, opts: { refetchInterval?: number } = {}) {
  return useQuery({
    ...invoiceQuery(id ?? ''),
    enabled: Boolean(id),
    refetchInterval: (q) => (q.state.data?.processing ? 1500 : (opts.refetchInterval ?? false)),
  })
}

export function usePayments(q: PaymentListQuery = {}) {
  return useQuery({
    queryKey: billingKeys.payments(q),
    queryFn: ({ signal }) => api.get<Page<Payment>>('/billing/payments', { query: q, signal }),
    placeholderData: keepPreviousData,
  })
}

/** One payment; polls every second while it is processing. */
export function usePayment(id: string | null) {
  return useQuery({
    queryKey: billingKeys.payment(id ?? ''),
    queryFn: ({ signal }) => api.get<Payment>(`/billing/payments/${id}`, { signal }),
    enabled: Boolean(id),
    refetchInterval: (q) => (q.state.data?.status === 'processing' ? 1000 : false),
  })
}
